const inlineNodeStyles = (sourceNode: Element, targetNode: Element) => {
  const computedStyles = window.getComputedStyle(sourceNode as HTMLElement);
  let styleText = "";

  for (let index = 0; index < computedStyles.length; index += 1) {
    const propertyName = computedStyles.item(index);
    const propertyValue = computedStyles.getPropertyValue(propertyName);
    styleText += `${propertyName}:${propertyValue};`;
  }

  targetNode.setAttribute("style", styleText);

  if (sourceNode instanceof HTMLImageElement && targetNode instanceof HTMLImageElement) {
    const imageWidth = Math.round(sourceNode.getBoundingClientRect().width);
    const imageHeight = Math.round(sourceNode.getBoundingClientRect().height);

    if (imageWidth > 0) {
      targetNode.setAttribute("width", `${imageWidth}`);
    }

    if (imageHeight > 0) {
      targetNode.setAttribute("height", `${imageHeight}`);
    }
  }

  const sourceChildren = Array.from(sourceNode.children);
  const targetChildren = Array.from(targetNode.children);

  for (let index = 0; index < sourceChildren.length; index += 1) {
    const sourceChild = sourceChildren[index];
    const targetChild = targetChildren[index];

    if (sourceChild && targetChild) {
      inlineNodeStyles(sourceChild, targetChild);
    }
  }
};

const toAbsoluteUrl = (url: string): string => {
  try {
    return new URL(url, window.location.href).href;
  } catch {
    return url;
  }
};

const blobToDataUrl = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.onloadend = () => resolve(String(fileReader.result));
    fileReader.onerror = () => reject(new Error("Falha ao converter blob para data URL"));
    fileReader.readAsDataURL(blob);
  });
};

const urlToDataUrl = async (url: string): Promise<string> => {
  const response = await fetch(url, { credentials: "same-origin" });

  if (!response.ok) {
    throw new Error("Falha ao buscar imagem para cópia da assinatura");
  }

  const imageBlob = await response.blob();
  return blobToDataUrl(imageBlob);
};

const normalizeTablesForEmail = (rootNode: HTMLElement) => {
  rootNode.querySelectorAll("table").forEach((tableNode) => {
    tableNode.setAttribute("cellpadding", "0");
    tableNode.setAttribute("cellspacing", "0");
    tableNode.setAttribute("border", "0");
    tableNode.setAttribute("role", "presentation");
  });
};

const normalizeAnchorsForEmail = (sourceRoot: HTMLElement, targetRoot: HTMLElement) => {
  const sourceAnchors = Array.from(sourceRoot.querySelectorAll("a"));
  const targetAnchors = Array.from(targetRoot.querySelectorAll("a"));

  for (let index = 0; index < targetAnchors.length; index += 1) {
    const sourceAnchor = sourceAnchors[index];
    const targetAnchor = targetAnchors[index];

    if (!targetAnchor) {
      continue;
    }

    const sourceHref = sourceAnchor?.href || targetAnchor.getAttribute("href") || "";
    if (sourceHref) {
      targetAnchor.setAttribute("href", toAbsoluteUrl(sourceHref));
    }
  }
};

const normalizeImagesForEmail = async (sourceRoot: HTMLElement, targetRoot: HTMLElement) => {
  const sourceImages = Array.from(sourceRoot.querySelectorAll("img"));
  const targetImages = Array.from(targetRoot.querySelectorAll("img"));

  for (let index = 0; index < targetImages.length; index += 1) {
    const sourceImage = sourceImages[index];
    const targetImage = targetImages[index];

    if (!targetImage) {
      continue;
    }

    const sourceRect = sourceImage?.getBoundingClientRect();
    const computedWidth = sourceRect ? Math.round(sourceRect.width) : 0;
    const computedHeight = sourceRect ? Math.round(sourceRect.height) : 0;

    if (computedWidth > 0) {
      targetImage.setAttribute("width", `${computedWidth}`);
    }

    if (computedHeight > 0) {
      targetImage.setAttribute("height", `${computedHeight}`);
    }

    const sourceUrl = sourceImage?.currentSrc || sourceImage?.src || targetImage.getAttribute("src") || "";

    if (!sourceUrl) {
      continue;
    }

    const absoluteImageUrl = toAbsoluteUrl(sourceUrl);
    targetImage.setAttribute("src", absoluteImageUrl);

    try {
      const imageDataUrl = await urlToDataUrl(absoluteImageUrl);
      targetImage.setAttribute("src", imageDataUrl);
    } catch {
      targetImage.setAttribute("src", absoluteImageUrl);
    }
  }
};

const buildCopyHtml = async (element: HTMLElement): Promise<{ html: string; text: string }> => {
  const clonedElement = element.cloneNode(true) as HTMLElement;

  inlineNodeStyles(element, clonedElement);

  clonedElement
    .querySelectorAll("[data-no-copy='true'], [data-no-copy]")
    .forEach((node) => node.remove());

  normalizeTablesForEmail(clonedElement);
  normalizeAnchorsForEmail(element, clonedElement);
  await normalizeImagesForEmail(element, clonedElement);

  return {
    html: clonedElement.outerHTML,
    text: clonedElement.innerText,
  };
};

const fallbackExecCommandCopy = (html: string): boolean => {
  const temporaryContainer = document.createElement("div");
  temporaryContainer.style.position = "fixed";
  temporaryContainer.style.left = "-99999px";
  temporaryContainer.style.top = "0";
  temporaryContainer.innerHTML = html;
  document.body.appendChild(temporaryContainer);

  const selection = window.getSelection();

  if (!selection) {
    document.body.removeChild(temporaryContainer);
    return false;
  }

  const range = document.createRange();
  range.selectNodeContents(temporaryContainer);

  selection.removeAllRanges();
  selection.addRange(range);

  let copied = false;

  try {
    copied = document.execCommand("copy");
  } catch {
    copied = false;
  }

  selection.removeAllRanges();
  document.body.removeChild(temporaryContainer);

  return copied;
};

export const copySignatureContent = async (element: HTMLElement): Promise<boolean> => {
  const { html, text } = await buildCopyHtml(element);

  try {
    if (navigator.clipboard && typeof ClipboardItem !== "undefined") {
      const clipboardItem = new ClipboardItem({
        "text/html": new Blob([html], { type: "text/html" }),
        "text/plain": new Blob([text], { type: "text/plain" }),
      });

      await navigator.clipboard.write([clipboardItem]);
      return true;
    }
  } catch {
    return fallbackExecCommandCopy(html);
  }

  if (fallbackExecCommandCopy(html)) {
    return true;
  }

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    return false;
  }

  return false;
};
